import React, { memo, useMemo } from 'react';
import { Icon, toIconsEnum } from 'components/Icons';
import { capitalize, isNotEmpty } from 'utility/functions';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';

import { SimpleCheckbox } from 'components/inputs/simpleCheckbox';
import { SportTicketAvvenimento } from 'features/sport/components/sportTicket/utils/calcoloCarrello/types';
import { SportTicketItemEsito } from 'features/sport/components/sportTicket/components/sportTicketItemEsito/SportTicketItemEsito';
import { avvenimentoUpdatedOnFisso } from 'features/sport/components/sportTicket/sportTicketSlice';
import { selectSistema } from 'features/sport/components/sportTicket/sportTicketSelectors';
import styles from './SportTicketItem.module.scss';

export type SportTicketItemProps = {
  avvenimento: SportTicketAvvenimento;
  cancelBtn?: boolean;
};

const PREFIX_ICON = 'DISCIPLINA';

const dtFormat = (dataOra: string): string => {
  if (!isNotEmpty(dataOra)) {
    return '';
  }

  const [data, ora] = `${dataOra}T`.split('T');
  const [h, m] = ora.split(':');

  const dt = `${data}`
    .split('-')
    .reverse()
    .map((x) => {
      const tmp = `00${x}`;
      return tmp.substring(tmp.length - 2);
    })
    .join('/');

  const hm = [h, m]
    .map((x) => {
      const tmp = `00${x}`;
      return tmp.substring(tmp.length - 2);
    })
    .join(':');

  return `${dt} ${hm}`;
};

export const SportTicketItem = memo(function SportTicketItem({ avvenimento, cancelBtn = true }: SportTicketItemProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isSistema = useTypedSelector(selectSistema);

  const {
    idDisciplina,
    descrizioneManifestazione,
    descrizioneManifestazioneTrKey,
    idProgramma,
    idAvvenimento,
    isFisso,
    esiti,
    dataOra,
    descrizioneTrKey,
    descrizione,
    live,
  } = avvenimento ?? {};

  const timeFormatted = useMemo(() => dtFormat(dataOra), [dataOra]);

  const disciplinaIcon = `${PREFIX_ICON}_${idDisciplina}`;

  const [labelPal, labelFixed] = useGetLabelsByKeys(['pal', 'fixed']);

  const handleChangeCheckbox = (element: HTMLInputElement) => {
    dispatch(
      avvenimentoUpdatedOnFisso({
        idAvvenimento: idAvvenimento!,
        isFisso: element.value === 'true' ? false : true,
      })
    );
  };

  return (
    <article className={styles.container}>
      <div className={styles.firstCol}>
        <ul className={styles.list}>
          <li className={`${styles.item} ${styles.smallText}`}>
            <Icon iconId={toIconsEnum(disciplinaIcon)} className={styles.icon} color={'var(--color-on-bg-secondary)'} />
            {t(descrizioneManifestazioneTrKey, descrizioneManifestazione)}
          </li>
          <li className={`${styles.item} ${styles.smallText}`}>{timeFormatted}</li>
        </ul>
        <p className={styles.competitors}>{capitalize(t(descrizioneTrKey, descrizione))}</p>
      </div>
      <div className={styles.secondCol}>
        <span className={styles.smallText}>
          {labelPal} {`${idProgramma}-${idAvvenimento}`}
        </span>
        {isSistema && (
          <SimpleCheckbox
            value={String(isFisso)}
            checked={String(isFisso) === 'true'}
            handleFilterChange={(e) => handleChangeCheckbox(e)}
            className={styles.checkbox}
            label={labelFixed}
          />
        )}
      </div>

      <ul className={`${styles.scommessaList} ${isSistema ? styles.hasBigMargin : ''}`}>
        <li className={styles.avvenimentoStatus}>
          {live?.status && <span className={styles.status}>{t(live.statusTrKey, live.status)}</span>}
          {live?.matchTime && <span className={styles.minutes}>{live?.matchTime}</span>}
          {live?.score?.firstCompetitor && live?.score?.secondCompetitor && (
            <>
              <span className={styles.pipe}>|</span>
              {live.score.lastSetScore?.firstCompetitor && live.score.lastSetScore?.secondCompetitor ? (
                <>
                  <span className={styles.esito}>
                    {live.score.lastSetScore.firstCompetitor}-{live.score.lastSetScore.secondCompetitor}
                  </span>
                  <span className={styles.esito}>
                    {`(${live?.score.firstCompetitor}-${live?.score.secondCompetitor})`}
                  </span>
                </>
              ) : (
                <span className={styles.esito}>{`${live?.score.firstCompetitor}-${live?.score.secondCompetitor}`}</span>
              )}
            </>
          )}
        </li>
        {esiti?.map((item) => (
          <SportTicketItemEsito key={`ticket-esito-${item.id}`} {...item} cancelBtn={cancelBtn} />
        ))}
      </ul>
    </article>
  );
});